/**
 * Configuracoes.
 *
 * A definicao mais importante desta tela e o TAMANHO DA LETRA. Ha dias em que
 * 22 px nao chegam, e a alternativa dela e nao conseguir usar o aplicativo. Por
 * isso esta em primeiro lugar, antes do tema e de tudo o resto.
 *
 * A chave do assistente fica salva no aparelho (configuracoes locais) e nunca
 * no pacote do aplicativo — quem a colar aqui e quem manda nela.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  BigButton,
  Card,
  List,
  ListItem,
  Screen,
  SectionHeading,
  Select,
  Stack,
  TextField,
  Toggle,
  useTextScale,
  useTheme,
} from '@flos/ui';
import { ROLE_LABELS, type FamilyRole } from '@flos/shared';
import { useApp } from '../state/AppContext';
import { AI_KEY_SETTING, AI_SETTINGS_NAMESPACE } from '../wiring/assistant';
import { readDisabledPlugins, writeDisabledPlugins } from '../wiring/preferences';
import { describeSync } from '../components/SyncIndicator';
import { SHELL_PATHS } from '../navigation';
import { exportAllData } from '../wiring/exportData';

type NotificationState = 'granted' | 'denied' | 'default' | 'unsupported';

function readNotificationState(): NotificationState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as NotificationState;
}

export function SettingsScreen() {
  const { boot, sync, manifests } = useApp();
  const navigate = useNavigate();
  const core = boot.core;

  const { theme, setTheme } = useTheme();
  const { scale, setScale } = useTextScale();

  const [apiKey, setApiKey] = useState(() =>
    core.settings.getSync<string>(AI_SETTINGS_NAMESPACE, AI_KEY_SETTING, ''),
  );
  const [keySaved, setKeySaved] = useState(false);
  const [disabled, setDisabled] = useState<string[]>(() => readDisabledPlugins());
  const [notifications, setNotifications] = useState<NotificationState>(readNotificationState);
  const [showLog, setShowLog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const session = core.session.peek();
  const allPlugins = core.registry.all();
  const syncWords = describeSync(sync);

  useEffect(() => {
    if (!keySaved) return;
    const timer = setTimeout(() => setKeySaved(false), 4000);
    return () => clearTimeout(timer);
  }, [keySaved]);

  const saveKey = async (): Promise<void> => {
    await core.settings.set(AI_SETTINGS_NAMESPACE, AI_KEY_SETTING, apiKey.trim());
    setKeySaved(true);
  };

  const togglePlugin = (pluginId: string, enabled: boolean): void => {
    const next = enabled ? disabled.filter((id) => id !== pluginId) : [...disabled, pluginId];
    setDisabled(next);
    writeDisabledPlugins(next);
    setNote('A alteração fica completa quando fechar e voltar a abrir o aplicativo.');
  };

  const askNotifications = async (): Promise<void> => {
    if (typeof Notification === 'undefined') return;
    const result = await Notification.requestPermission();
    setNotifications(result as NotificationState);
  };

  const doExport = async (): Promise<void> => {
    setBusy(true);
    try {
      await exportAllData(core);
      setNote('Os seus dados foram salvos num arquivo no aparelho.');
    } catch (error) {
      setNote(
        error instanceof Error
          ? `Não consegui exportar: ${error.message}`
          : 'Não consegui exportar os dados.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen title="Configurações" back={() => navigate(SHELL_PATHS.HOME)}>
      <Stack gap="lg">
        {note ? (
          <Banner tone="info" title="Nota" onDismiss={() => setNote(null)}>
            {note}
          </Banner>
        ) : null}

        {/* ---- O que mais importa ---- */}
        <Card title="Tamanho da letra" icon="🔠">
          <Stack gap="md">
            <p>Se estiver custando a ler, aumente aqui. Muda o aplicativo todo.</p>
            <Toggle
              label="Letra maior"
              checked={scale === 'large'}
              onChange={(checked) => setScale(checked ? 'large' : 'normal')}
              hint="Aumenta todo o texto e todos os botões cerca de um quarto."
            />
          </Stack>
        </Card>

        <Card title="Aspecto" icon="🌗">
          <Select
            label="Tema"
            value={theme}
            onChange={(value) => setTheme(value as 'light' | 'dark' | 'system')}
            options={[
              { value: 'light', label: 'Claro' },
              { value: 'dark', label: 'Escuro' },
              { value: 'system', label: 'Automático (segue o celular)' },
            ]}
          />
        </Card>

        {/*
          Região e moeda deixaram de ser uma escolha: o aplicativo é do Brasil.
          Continua escrito aqui porque quem confere um valor tem o direito de
          saber em que moeda e em que fuso ele está — calar isso não é
          simplificar, é esconder.
        */}
        <Card title="Região e moeda" icon="🌍">
          <List label="Formato de datas e dinheiro">
            <ListItem title="Brasil" subtitle="Idioma e formato" icon="🇧🇷" />
            <ListItem title="Real (R$)" subtitle="Moeda" icon="💵" />
            <ListItem title="Horário de Brasília" subtitle="Fuso horário" icon="🕐" />
          </List>
        </Card>

        {/* ---- Perfil ---- */}
        <Card title="O seu perfil" icon="👤">
          {session ? (
            <List label="Dados do perfil">
              <ListItem title={session.displayName} subtitle="Nome" icon="🙂" />
              <ListItem
                title={ROLE_LABELS[session.role as FamilyRole] ?? session.role}
                subtitle="O que pode fazer na família"
                icon="🛡️"
              />
              <ListItem title={session.familyId} subtitle="Família" icon="🏡" />
            </List>
          ) : (
            <Stack gap="md">
              <p>Ainda não entrou com o seu perfil.</p>
              <BigButton icon="➡️" onClick={() => navigate(SHELL_PATHS.ONBOARDING)}>
                Entrar
              </BigButton>
            </Stack>
          )}
        </Card>

        {/* ---- Avisos ---- */}
        <Card title="Avisos no celular" icon="🔔">
          <Stack gap="md">
            {notifications === 'granted' ? (
              <p>Os avisos estão ligados. Vai receber os lembretes na hora certa.</p>
            ) : notifications === 'denied' ? (
              <p>
                Os avisos estão bloqueados nas configurações do celular. Para os
                voltar a ligar, abra as configurações do navegador para este aplicativo.
              </p>
            ) : notifications === 'unsupported' ? (
              <p>Este aparelho não sabe mostrar avisos deste aplicativo.</p>
            ) : (
              <>
                <p>Sem avisos, os lembretes só aparecem quando abrir o aplicativo.</p>
                <BigButton icon="🔔" onClick={() => void askNotifications()}>
                  Ligar os avisos
                </BigButton>
              </>
            )}
          </Stack>
        </Card>

        {/* ---- Sincronizacao ---- */}
        <Card title="Cópia de segurança" icon="☁️">
          <Stack gap="md">
            <p>
              <span className="flos-icon" aria-hidden="true">
                {syncWords.icon}
              </span>{' '}
              {syncWords.word} — {syncWords.detail}
            </p>
            {sync.conflicts > 0 ? (
              <BigButton
                icon="⚖️"
                variant="secondary"
                onClick={() => navigate(SHELL_PATHS.CONFLICTS)}
              >
                Rever diferenças
              </BigButton>
            ) : null}
            <BigButton
              icon="🔄"
              variant="secondary"
              onClick={() => void boot.sync.syncNow()}
              disabled={!sync.online}
            >
              Enviar agora
            </BigButton>
            <BigButton icon="💾" variant="secondary" loading={busy} onClick={() => void doExport()}>
              Exportar todos os meus dados
            </BigButton>
          </Stack>
        </Card>

        {/* ---- Assistente ---- */}
        <Card title="Assistente" icon="🎤">
          <Stack gap="md">
            <TextField
              label="Chave do assistente"
              value={apiKey}
              onChange={setApiKey}
              hint="Fica salva só neste aparelho. Se não tiver, peça a quem administra a família."
              placeholder="Colar aqui"
            />
            <BigButton icon="💾" onClick={() => void saveKey()}>
              Salvar chave
            </BigButton>
            {keySaved ? (
              <Banner tone="success" title="Chave salva">
                O assistente fica pronto quando voltar a abrir o aplicativo.
              </Banner>
            ) : null}
          </Stack>
        </Card>

        {/* ---- Plugins ---- */}
        <Stack gap="sm">
          <SectionHeading>Áreas do aplicativo</SectionHeading>
          <Card>
            <Stack gap="md">
              {allPlugins.length === 0 ? (
                <p>Ainda não há nenhuma área instalada.</p>
              ) : (
                allPlugins.map((entry) => (
                  <Toggle
                    key={entry.manifest.id}
                    label={`${entry.manifest.icon} ${entry.manifest.name}`}
                    checked={!disabled.includes(entry.manifest.id)}
                    onChange={(checked) => togglePlugin(entry.manifest.id, checked)}
                    hint={
                      entry.state === 'loaded'
                        ? entry.manifest.description
                        : `Não abriu: ${entry.problems.join('; ') || 'motivo desconhecido'}`
                    }
                  />
                ))
              )}
            </Stack>
          </Card>
        </Stack>

        {/* ---- Diagnostico ---- */}
        <Card title="Diagnóstico" icon="🩺">
          <Stack gap="md">
            {boot.failedPlugins.length > 0 ? (
              <Banner tone="warning" title="Partes que não abriram">
                <List label="Partes com problemas">
                  {boot.failedPlugins.map((plugin) => (
                    <ListItem key={plugin.id} title={plugin.name} subtitle={plugin.reason} icon="⚠️" />
                  ))}
                </List>
              </Banner>
            ) : (
              <p>Tudo abriu sem problemas. O aplicativo demorou {boot.elapsedMs} ms a arrancar.</p>
            )}
            <BigButton variant="ghost" icon="📋" onClick={() => setShowLog((value) => !value)}>
              {showLog ? 'Esconder registro técnico' : 'Ver registro técnico'}
            </BigButton>
            {showLog ? (
              <pre className="app-log" aria-label="Registro técnico">
                {core.logger
                  .recent(120)
                  .map((entry) => `${entry.at} ${entry.level.toUpperCase()} ${entry.msg}`)
                  .join('\n') || 'Sem registros.'}
              </pre>
            ) : null}
          </Stack>
        </Card>

        <p className="app-footnote">
          {manifests.length === 1
            ? '1 área ativa'
            : `${manifests.length} áreas ativas`}{' '}
          · versão {import.meta.env.MODE}
        </p>
      </Stack>
    </Screen>
  );
}

export default SettingsScreen;
